import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import nfLogoWhite from '@assets/images/icons/noteflight-logo-white.svg'
import nfLogo from '@assets/images/icons/noteflight-logo.svg'
import useOutsideClick from '@hooks/useOutsideClick'
import Divider from '@layout/Divider'
import { useSession } from '@stores/globalData/globalData.store'

import CommunityNav from './CommunityNav'
import CompanyNav from './CompanyNav'
import GroupsNav from './GroupsNav'
import HelpNav from './HelpNav'
import InfoNav from './InfoNav'
import LegalNav from './LegalNav'
import MainNav from './MainNav'
import PurchaseNav from './PurchaseNav'
import RecentScoresNav from './RecentScoresNav'
import SocialNav from './SocialNav'

interface Props {
  show?: boolean
  onHide: () => void
}

const SideNavMenu: React.FC<Props> = ({ show, onHide }: Props) => {
  const { t } = useTranslation()
  const session = useSession()
  const closeRef = useRef<HTMLButtonElement>(null)

  const menuRef = useOutsideClick<HTMLDivElement>((elem) => {
    onHide()
    elem?.scrollTo({ top: 0 })
  })

  // Auto-focus to close element (for accessibility)
  useEffect(() => {
    if (closeRef.current && show) {
      setTimeout(() => {
        closeRef.current?.focus()
      }, 250)
    }
  }, [show])

  return (
    <>
      {show && <div className="bg-black bg-opacity-20 inset-0 fixed md:hidden z-30" />}
      <div
        role="navigation"
        ref={menuRef}
        data-testid="side-nav"
        className={`${
          show ? 'visible left-0' : 'invisible left-[-310px] sm:left-[-260px]'
        } absolute top-0 w-full max-w-[300px] sm:max-w-[250px] h-screen bg-blue-dark learn:bg-green dark:bg-black-panel text-white shadow-thick z-50 white whitespace-nowrap overflow-x-hidden transition-left duration-300 overflow-y-auto`}>
        <button
          type="button"
          className="absolute top-0 right-0 mt-6 mr-5 text-white"
          onClick={onHide}
          ref={closeRef}>
          <i className="nf-icon nf-close text-lg" />
        </button>

        <div className="w-full p-2">
          <img
            src={session?.plan === 'learn' ? nfLogoWhite : nfLogo}
            alt="Noteflight"
            className="px-4 pt-3"
            role="image"
          />
          {session.isSite() ? (
            <>
              <p className="px-4 mt-1 text-xs font-normal">{session.community.name}</p>
              <Divider />
            </>
          ) : (
            <div className="mt-6" />
          )}

          <CommunityNav />
          <MainNav />
          <RecentScoresNav />
          <GroupsNav />
          <HelpNav />
          <PurchaseNav />
          <CompanyNav />
          <InfoNav />
          <SocialNav />
          <LegalNav />

          <p className="text-xs my-12 px-3 whitespace-normal">
            {t('content.noteflightCopyright', {
              year: new Date().getFullYear(),
            })}
          </p>
        </div>
      </div>
    </>
  )
}

export default SideNavMenu
